import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useEffect, useMemo, useState } from 'react';
import { slugify } from '@/lib/slugify';
import { getLocationSlugPaths } from '@/lib/locationStaticPaths';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SLUG = 'scrap-yards-near-me';
const DEFAULT_CATEGORY_SLUG = 'scrap-yards';
const CATEGORY_ID = '5020';
const BRAND_LABEL = 'Scrap Yard';
const BRAND_LABEL_PLURAL = 'Scrap Yards';

const ALL_PROVINCE_SLUGS = [
    'gauteng', 'western-cape', 'kwazulu-natal', 'eastern-cape',
    'free-state', 'mpumalanga', 'limpopo', 'north-west', 'northern-cape',
];

const PROVINCE_DISPLAY_NAMES: Record<string, string> = {
    'gauteng': 'Gauteng', 'western-cape': 'Western Cape', 'kwazulu-natal': 'KwaZulu-Natal',
    'eastern-cape': 'Eastern Cape', 'free-state': 'Free State', 'mpumalanga': 'Mpumalanga',
    'limpopo': 'Limpopo', 'north-west': 'North West', 'northern-cape': 'Northern Cape',
};

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const quickProvinces = [
    'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
    'Free State', 'Mpumalanga', 'Limpopo', 'North West', 'Northern Cape',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toTitleCase(slug: string): string {
    return slug.split('-').filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ScrapYardsSlugPage() {
    const router = useRouter();

    // In a static export the shell HTML's __NEXT_DATA__ always contains
    // query: { slug: ['sss-shell'] }. Derive real path segments from router.asPath
    // which Next.js reconciles to the actual browser URL after hydration.
    // `mounted` ensures the initial client render matches the server render (both use empty slug)
    // so React does not throw a hydration mismatch error.
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const BASE_SLUG_PATH = `/${PAGE_SLUG}/`;
    const rawSlug: string[] = useMemo(() => {
        if (!mounted || !router.isReady) return [];
        const path = router.asPath.split('?')[0].replace(/\/$/, '');
        if (path.startsWith(BASE_SLUG_PATH)) {
            const segs = path.slice(BASE_SLUG_PATH.length).split('/').filter(Boolean);
            if (segs.length > 0 && segs[0] !== 'sss-shell') return segs;
        }
        const querySlug = (router.query.slug as string[] | undefined) ?? [];
        return querySlug.filter((s) => s !== 'sss-shell');
    }, [mounted, router.isReady, router.asPath, router.query.slug]);

    // URL format: /[page-slug]/[province]/[city?]/[suburb?]
    const [provinceSlug = '', citySlug = '', suburbSlug = ''] = rawSlug;

    const provinceName = PROVINCE_DISPLAY_NAMES[provinceSlug] ?? (provinceSlug ? toTitleCase(provinceSlug) : '');
    const cityName = citySlug ? toTitleCase(citySlug) : '';
    const suburbName = suburbSlug ? toTitleCase(suburbSlug) : '';
    const hasLocation = Boolean(provinceSlug || citySlug || suburbSlug);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;
    const locationSuffix = hasLocation ? `in ${locationLabel}` : 'in South Africa';
    const keywordList = [
        `${BRAND_LABEL_PLURAL.toLowerCase()} ${locationLabel || 'south africa'}`,
        `${BRAND_LABEL_PLURAL.toLowerCase()} near me`,
        `${BRAND_LABEL.toLowerCase()} near me`,
    ].join(', ');

    const pageTitle = hasLocation
        ? `${BRAND_LABEL_PLURAL} ${locationSuffix} | ${BRAND_LABEL} Near Me`
        : `${BRAND_LABEL_PLURAL} in South Africa | ${BRAND_LABEL} Near Me`;

    const pageDescription = hasLocation
        ? `Find trusted scrap yards ${locationSuffix}. Browse profiles of local scrap dealers, auto dismantlers, and used car parts suppliers near you. Search by city or province now.`
        : 'Find trusted scrap yards in South Africa. Browse local scrap dealers, auto dismantlers, and used car parts suppliers. Locate scrapyards near me by province or city now.';

    const canonicalParts = ['', PAGE_SLUG];
    if (provinceSlug) canonicalParts.push(provinceSlug);
    if (citySlug) canonicalParts.push(citySlug);
    if (suburbSlug) canonicalParts.push(suburbSlug);
    const canonicalUrl = `${ENV.DOMAIN_URL}${canonicalParts.join('/')}`;
    const baseSlugPath = `/${PAGE_SLUG}`;

    const searchParams = {
        category: '',
        province: provinceSlug,
        city: citySlug,
        suburb: suburbSlug,
        text: '',
        page: 1,
    };

    const faqItems = [
        {
            question: `How do I find scrap yards ${locationSuffix}?`,
            answer: hasLocation
                ? `You are viewing ${BRAND_LABEL_PLURAL} ${locationSuffix}. Use the province and city filters to widen or narrow results.`
                : 'Use the province and city filters to narrow results. You can also search by part type or vehicle model.',
        },
        {
            question: 'What parts can I find at scrap yards?',
            answer: 'Scrap yards stock used engines, gearboxes, body panels, doors, bonnets, bumpers, electrical components, suspension parts, axles, and more at a fraction of new part prices.',
        },
        {
            question: 'Are used parts from scrap yards reliable?',
            answer: 'Quality varies by yard. Buy from reputable dismantlers, inspect parts before purchase, and ask about any warranties offered. Many yards test mechanical units before sale.',
        },
        {
            question: 'How do I contact a scrapyard listing?',
            answer: 'Click any listing to view contact details, address, operating hours, and available parts. You can call ahead to confirm part availability before visiting.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywordList} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'LocalBusiness',
                                    '@id': canonicalUrl,
                                    name: `${BRAND_LABEL_PLURAL} ${locationSuffix}`,
                                    description: pageDescription,
                                    url: canonicalUrl,
                                    areaServed: locationLabel || 'South Africa',
                                },
                                {
                                    '@type': 'BreadcrumbList',
                                    itemListElement: [
                                        { '@type': 'ListItem', position: 1, name: BRAND_LABEL_PLURAL, item: `${ENV.DOMAIN_URL}/${PAGE_SLUG}` },
                                        ...(provinceSlug ? [{ '@type': 'ListItem', position: 2, name: provinceName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}` }] : []),
                                        ...(citySlug ? [{ '@type': 'ListItem', position: 3, name: cityName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}/${citySlug}` }] : []),
                                        ...(suburbSlug ? [{ '@type': 'ListItem', position: 4, name: suburbName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}/${citySlug}/${suburbSlug}` }] : []),
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: { '@type': 'Answer', text: item.answer },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>

            {/* Hero */}
            <section className="relative overflow-hidden bg-[#1a1200] text-white">
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            {hasLocation && (
                                <nav className="mb-3 flex flex-wrap items-center gap-1 text-sm text-amber-200">
                                    <Link href={`/${PAGE_SLUG}`} className="hover:text-white">{BRAND_LABEL_PLURAL}</Link>
                                    {provinceSlug && (
                                        <>
                                            <span>/</span>
                                            <Link href={`${baseSlugPath}/${provinceSlug}`} className="hover:text-white">{provinceName}</Link>
                                        </>
                                    )}
                                    {citySlug && (
                                        <>
                                            <span>/</span>
                                            <span className="text-white">{cityName}</span>
                                        </>
                                    )}
                                </nav>
                            )}
                            {!hasLocation && (
                                <p className="mb-3 text-xs uppercase tracking-[0.3em] text-amber-200">Auto Dismantlers Directory</p>
                            )}
                            <h1 className="text-3xl font-bold leading-tight md:text-4xl">
                                {BRAND_LABEL_PLURAL} {locationSuffix}
                            </h1>
                            <p className="mt-4 text-base text-amber-100 md:text-lg">
                                Find local scrap yards, auto dismantlers, and used car parts suppliers near you.
                                Compare listings, check part availability, and contact yards directly.
                            </p>
                            <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                <Link href={`${baseSlugPath}/gauteng/johannesburg`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300">Scrapyards Johannesburg</Link>
                                <Link href={`${baseSlugPath}/western-cape/cape-town`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300">Scrapyards Cape Town</Link>
                                <Link href={`${baseSlugPath}/kwazulu-natal/durban`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300">Scrapyards Durban</Link>
                                <Link href={`${baseSlugPath}/gauteng/pretoria`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300">Scrapyards Pretoria</Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                                <h2 className="text-lg font-semibold">Browse by Province</h2>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {quickProvinces.map((p) => (
                                        <Link key={p} href={`${baseSlugPath}/${slugify(p)}`} className="rounded-full border border-white/20 px-3 py-1.5 text-sm hover:border-amber-300 hover:text-amber-200">{p}</Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Business Listings */}
            <div className="my-10">
                <div className="container">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds={CATEGORY_ID}
                        initialBusinesses={[]}
                        initialPagination={{ count: 0, currentPage: 1, totalPages: 0 }}
                    />
                </div>
            </div>

            {/* Browse Cities */}
            <section className="container my-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-slate-900">{BRAND_LABEL_PLURAL} by City</h2>
                    <p className="mt-2 text-sm text-slate-600">Jump to popular cities to find scrap yards near you.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {quickCities.map((city) => (
                            <Link key={city.name} href={`${baseSlugPath}/${city.province}/${slugify(city.name)}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:border-amber-400 hover:text-amber-700">
                                {`${BRAND_LABEL_PLURAL} in ${city.name}`}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="container my-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-slate-900">Frequently Asked Questions</h2>
                    <div className="mt-4 space-y-4">
                        {faqItems.map((item) => (
                            <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <DownloadApp />
        </>
    );
}

// ─── Static generation ────────────────────────────────────────────────────────

export const getStaticPaths: GetStaticPaths = () => ({
    paths: [
        { params: { slug: ['sss-shell'] } },
        ...getLocationSlugPaths(3).map((slug) => ({ params: { slug } })),
    ],
    // false in production (static-export requirement); every province/city/suburb
    // is pre-generated above so no runtime fallback is needed.
    // blocking in dev so dynamic paths resolve during local development.
    fallback: process.env.NODE_ENV === 'production' ? false : 'blocking',
});

export const getStaticProps: GetStaticProps = async () => {
    return { props: {} };
};
